<template>
  <div>
    <v-progress-linear v-if="cargando" indeterminate color="purple" class="mb-2" />

    <ag-grid-vue
      :gridOptions="gridOptions"
      class="ag-theme-balham ag-grid-listado-pagos-paginados"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      @grid-ready="onGridReady"
      :rowData="items"
      rowSelection="single"
      colResizeDefault="shift"
      :animateRows="false"
      :floatingFilter="true"
      :pagination="false"
      :rowClassRules="rowClassRules"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
      :alwaysShowVerticalScroll="true"
      :suppressPaginationPanel="true">
    </ag-grid-vue>
  </div>
</template>
  
<script>
import { mapGetters } from 'vuex';
import listadoAgGridMixin from '../mixins/listado-ag-grid.mixin';
import { formatearValorAMoneda, valueFormatterMoneda } from '../utils/funciones';

export default {
  name: 'ListadoSolicitudesPagosPaginados',
  mixins: [listadoAgGridMixin],
  mounted() {
    this.toggleColumnVisibility('headerName', 'DETALLE', this.getMostradoDetalleSolicitudesPagos);
  },
  data() {
    return {
      columnDefs: [
        {
          headerName: 'N° SOLICITUD',
          field: 'numeroSolicitud',
          filter: true,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: false,
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'PROGRAMA UNIVERSIDAD',
          field: 'programaUniversidad.programaNombre',
          minWidth: 350,
        },
        {
          headerName: 'ESTADO',
          cellRenderer: (params) => {
            const estudiantesSolicitados = params.data?.detallesSolicitudesPagos?.length || 0;
            const estudiantesPagados =
              params.data?.pagosUniversidades.reduce(
                (acc, pago) => acc + Number(pago.detallesPagosUniversidades.length),
                0,
              ) || 0;
            let estado = '';
            let color = '';

            if (estudiantesPagados === 0) {
              estado = 'PENDIENTE';
              color = 'red';
            } else if (estudiantesPagados < estudiantesSolicitados) {
              estado = 'PARCIAL';
              color = 'orange';
            } else {
              estado = 'PAGADO';
              color = 'green';
            }

            return /*html*/ `
              <span class="v-chip v-chip--outlined v-chip--pill theme--dark v-size--x-small ${color} ${color}--text body-2">
                  <span class="v-chip__content"> ${estado} </span>
              </span>
            `;
          },
          valueGetter: (params) => {
            const estudiantesSolicitados = params.data?.detallesSolicitudesPagos?.length || 0;
            const estudiantesPagados =
              params.data?.pagosUniversidades.reduce(
                (acc, pago) => acc + Number(pago.detallesPagosUniversidades.length),
                0,
              ) || 0;
            let estado = '';

            if (estudiantesPagados === 0) {
              estado = 'PENDIENTE';
            } else if (estudiantesPagados < estudiantesSolicitados) {
              estado = 'PARCIAL';
            } else {
              estado = 'PAGADO';
            }

            return estado;
          },
          minWidth: 150,
          maxWidth: 150,
        },
        {
          headerName: 'SEDE UNIVERSIDAD',
          field: 'programaUniversidad.sedeUniversidad.nombre',
          minWidth: 150,
          maxWidth: 150,
        },
        {
          headerName: 'N° CUOTA',
          field: 'detallePlanPagoConceptoUniversidad.numeroCuota',
          type: 'numericColumn',
          filter: false,
          minWidth: 100,
          maxWidth: 100,
        },
        {
          headerName: 'CONCEPTO',
          field: 'detallePlanPagoConceptoUniversidad.planPagoConceptoUniversidad.conceptoUniversidad.concepto.nombre',
          filter: false,
          minWidth: 150,
          maxWidth: 150,
        },
        {
          headerName: 'MONTO',
          field: 'monto',
          type: 'numericColumn',
          valueFormatter: valueFormatterMoneda,
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'N° EST. SOLIC.',
          cellRenderer: (params) => {
            const estudiantesSolicitados = params.data?.detallesSolicitudesPagos?.length || 0;

            return estudiantesSolicitados;
          },
          type: 'numericColumn',
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'MONTO SOLIC.',
          cellRenderer: (params) => {
            const monto = params.data?.monto || 0;
            const estudiantesSolicitados = params.data?.detallesSolicitudesPagos?.length || 0;
            const montoSolicitado = (monto * estudiantesSolicitados).toFixed(2);

            return formatearValorAMoneda(montoSolicitado);
          },
          type: 'numericColumn',
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'N° EST. PAGADOS',
          cellRenderer: (params) => {
            const estudiantesPagados =
              params.data?.pagosUniversidades.reduce(
                (acc, pago) => acc + Number(pago.detallesPagosUniversidades.length),
                0,
              ) || 0;

            return estudiantesPagados;
          },
          type: 'numericColumn',
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'MONTO PAGADO',
          cellRenderer: (params) => {
            const estudiantesPagados =
              params.data?.pagosUniversidades.reduce(
                (acc, pago) => acc + Number(pago.detallesPagosUniversidades.length),
                0,
              ) || 0;
            const montoPagado = (params.data?.monto * estudiantesPagados).toFixed(2);

            return formatearValorAMoneda(montoPagado);
          },
          type: 'numericColumn',
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'N° PAGOS',
          cellRenderer: (params) => {
            return params.data?.pagosUniversidades?.length || 0;
          },
          type: 'numericColumn',
          filter: false,
          minWidth: 125,
          maxWidth: 125,
        },
        {
          headerName: 'SEDE',
          field: 'sede.nombre',
          minWidth: 150,
          maxWidth: 150,
        },
        {
          headerName: 'FECHA SOLICITUD',
          field: 'fechaSolicitud',
          minWidth: 150,
          maxWidth: 150,
        },
        {
          headerName: 'DETALLE',
          headerComponentParams: {
            template: /* html */ `
              <table width="100%" style="border-collapse">
                <tr>
                  <th style="font-size: 13px; font-weight: 600; width: 70px;">#</th>
                  <th style="font-size: 13px; font-weight: 600; width: 150px;">N° DOCUMENTO</th>
                  <th style="font-size: 13px; font-weight: 600; width: 150px;">PRIMER APELLIDO</th>
                  <th style="font-size: 13px; font-weight: 600; width: 150px;">SEGUNDO APELLIDO</th>
                  <th style="font-size: 13px; font-weight: 600; width: 150px;">NOMBRES</th>
                  <th style="font-size: 13px; font-weight: 600; width: 150px;">ESTA PAGADO</th>
                  <th style="font-size: 13px; font-weight: 600; width: 450px;">PROGRAMA</th>
                </tr>
              </table>
            `,
          },
          cellRenderer: (params) => {
            if (params.node.rowPinned) {
              return undefined;
            }

            let detalle = /* html*/ `
            <table width="100%" style="border-collapse: collapse;">`;

            for (let index = 0; index < params.data.detallesSolicitudesPagos.length; index++) {
              const detalleSolicitudPago = params.data.detallesSolicitudesPagos[index];
              const inscripcion = detalleSolicitudPago.inscripcionProgramaUniversidad.inscripcion;
              const estudiante = inscripcion.estudiante;
              const programa = inscripcion.programa;

              detalle += /* html*/ `
                <tr>
                  <td style="width: 70px; max-width: 70px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${index + 1}
                  </td>
                  <td style="width: 150px; max-width: 150px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${estudiante.numeroDocumento}
                  </td>
                  <td style="width: 150px; max-width: 150px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${estudiante.primerApellido}
                  </td>
                  <td style="width: 150px; max-width: 150px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${estudiante.segundoApellido}
                  </td>
                  <td style="width: 150px; max-width: 150px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${estudiante.nombres}
                  </td>
                  <td style="width: 150px; max-width: 150px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    <span class="v-chip v-chip--outlined theme--dark v-size--x-small ${
                      detalleSolicitudPago.detallePagoUniversidad ? 'green' : 'red'
                    } ${detalleSolicitudPago.detallePagoUniversidad ? 'green' : 'red'}--text" file="v-chip/usage">
                      <span class="v-chip__content">
                      ${detalleSolicitudPago.detallePagoUniversidad ? 'PAGADO' : 'NO PAGADO'}
                      </span>
                    </span>
                  </td>
                  <td style="width: 450px; max-width: 450px; overflow-x: hidden; border-bottom: 1px solid #d9dcde;">
                    ${programa.nombreCompuesto}
                  </td>
                </tr>`;
            }

            detalle += /* html*/ `</table>`;

            return detalle;
          },
          valueGetter: (params) => {
            if (params.node.rowPinned) {
              return undefined;
            }

            let detalle = '';

            for (let index = 0; index < params.data.detallesSolicitudesPagos.length; index++) {
              const detalleSolicitudPago = params.data.detallesSolicitudesPagos[index];
              const inscripcion = detalleSolicitudPago.inscripcionProgramaUniversidad.inscripcion;
              const estudiante = inscripcion.estudiante;
              const programa = inscripcion.programa;

              detalle += /* html*/ `
                ${estudiante.numeroDocumento}
                ${estudiante.primerApellido}
                ${estudiante.segundoApellido}
                ${estudiante.nombres}
                ${detalleSolicitudPago.detallePagoUniversidad ? 'PAGADO' : 'NO PAGADO'}
                ${programa.nombreCompuesto}
              `;
            }

            return detalle;
          },
          filter: true,
          minWidth: 1270,
        },
        {
          headerName: 'CREADO EN',
          field: 'creadoEn',
          filter: false,
          minWidth: 200,
          maxWidth: 200,
        },
        {
          headerName: 'ACTUALIZADO EN',
          field: 'actualizadoEn',
          filter: false,
          minWidth: 200,
          maxWidth: 200,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getMostradoDetalleSolicitudesPagos: 'listado/getMostradoDetalleSolicitudesPagos',
    }),
  },
  watch: {
    getMostradoDetalleSolicitudesPagos(value) {
      this.toggleColumnVisibility('headerName', 'DETALLE', value);
    },
  },
};
</script>
  
<style>
@import '~ag-grid-community/dist/styles/ag-grid.css';
@import '~ag-grid-community/dist/styles/ag-theme-balham.css';

.ag-grid-listado-pagos-paginados {
  height: calc(var(--vh, 1vh) * 100 - 19em);
}
</style>
  